// src/features/live/meta.ts — static tables + display helpers for the Live page.
//
// Chip a11y model note: mirrors videos/meta.ts's documented per-platform split —
// web (LiveChips.tsx) uses `role="group"` + `aria-pressed` (filter-toggle idiom);
// mobile (LiveScreen.tsx) uses `accessibilityRole="tablist"`/`"tab"`, matching
// VideosScreen's chip-row convention. Deliberate, not drift.
//
// The concept spec's "overflow" chip was deliberately dropped, matching the
// Videos precedent (VideoChips) — both chip rows use a horizontal-scroll
// pattern instead of an overflow menu.
//
// `MOBILE_CHIPS` is consumed by meta.test.ts's assertions and documents the
// mobile subset; mobile's own chip list lives in mobile/src/core/lib/
// liveMeta.ts (re-declared there, not imported — no cross-package import
// exists between the two apps) with a "keep in sync" comment pointing back
// here.
import type { LiveChipId } from '@/core/models/live';

export interface ChipDef {
  id: LiveChipId;
  label: string;
}

/** The web chip row (full set). */
export const WEB_CHIPS: ChipDef[] = [
  { id: 'all', label: 'All' },
  { id: 'gaming', label: 'Gaming' },
  { id: 'news-politics', label: 'News & Politics' },
  { id: 'technology', label: 'Technology' },
  { id: 'education', label: 'Education' },
  { id: 'entertainment', label: 'Entertainment' },
  { id: 'music', label: 'Music' },
  { id: 'talk', label: 'Talk' },
];

/** Mobile shows a deliberately smaller 6-chip row (owner decision — no Education
 *  or Entertainment there), same idea as Videos' 4-of-10 mobile subset. */
export const MOBILE_CHIPS: ChipDef[] = WEB_CHIPS.filter(
  (c) => c.id !== 'education' && c.id !== 'entertainment',
);

/** Compact viewer-count display, e.g. 8200 -> "8.2K watching". */
export function formatViewers(n: number): string {
  if (n < 1000) return `${n} watching`;
  if (n < 1_000_000) return `${trim(n / 1000)}K watching`;
  return `${trim(n / 1_000_000)}M watching`;
}

function trim(value: number): string {
  return value.toFixed(1).replace(/\.0$/, '');
}

/**
 * The explicit "preview — nothing here is really live" affordance the owner
 * decided the page needs (slice-4-xl §2 "Open"): LIVE badges and frozen viewer
 * counts assert real-time state that does not exist, and without this line a
 * card reading "LIVE · 8.2K watching" reads as a broken product, not an
 * unbuilt one.
 */
export const PREVIEW_NOTICE =
  "Preview: nothing here is really live yet. Badges, viewer counts, and chat are seeded placeholders.";

/**
 * Darker red than theme.color.danger, used ONLY for the LIVE badge's white-on-
 * red fill. theme.color.danger (#ff5d6c) measures ~2.97:1 with white text —
 * below WCAG AA's 4.5:1 for normal text. This fixed value (not theme-derived,
 * same precedent as the existing rgba(0,0,0,0.72) viewer-count pill) measures
 * ~6:1. Mirrored on mobile as core/lib/liveMeta.ts's LIVE_BADGE_BG.
 */
export const LIVE_BADGE_BG = '#c41230';
